import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
} from "react";
import { AnimatePresence, motion } from "motion/react";
import type { ReactElement, ReactNode } from "react";

interface MultiStepFormContextType {
  currentStepIndex: number;
  goTo: (index: number) => void;
  next: () => void;
  prev: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
  step: ReactElement;
}

const MultiStepFormContext = createContext<MultiStepFormContextType | null>(
  null,
);

export function useMultiStepForm() {
  const context = useContext(MultiStepFormContext);
  if (!context) {
    throw new Error(
      "useMultiStepForm must be used within a MultiStepForm component",
    );
  }
  return context;
}

interface MultiStepFormProps {
  children: ReactNode;
}

export function MultiStepForm({ children }: MultiStepFormProps) {
  const steps = React.Children.toArray(children) as ReactElement[];
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const goTo = useCallback(
    (index: number) => {
      if (index >= 0 && index < steps.length) {
        setCurrentStepIndex(index);
      }
    },
    [steps.length],
  );

  const next = useCallback(() => {
    setCurrentStepIndex((i) => {
      if (i >= steps.length - 1) return i;
      return i + 1;
    });
  }, [steps.length]);

  const prev = useCallback(() => {
    setCurrentStepIndex((i) => {
      if (i <= 0) return i;
      return i - 1;
    });
  }, []);

  const value = useMemo(
    () => ({
      currentStepIndex,
      step: steps[currentStepIndex],
      goTo,
      next,
      prev,
      isFirstStep: currentStepIndex === 0,
      isLastStep: currentStepIndex === steps.length - 1,
    }),
    [currentStepIndex, steps, goTo, next, prev],
  );

  return (
    <MultiStepFormContext.Provider value={value}>
      <div className="overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStepIndex}
            initial={{ x: 300, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -300, opacity: 0 }}
            transition={{
              duration: 0.4,
              ease: [0.25, 0.46, 0.45, 0.94], // Custom cubic-bezier for smooth easing
            }}
          >
            {steps[currentStepIndex]}
          </motion.div>
        </AnimatePresence>
      </div>
    </MultiStepFormContext.Provider>
  );
}
